<!--

	ReferralsPage.svelte

    Contains a <ReferralSections> component which manages different sections.
    Each section contains questions and users are prompted to move forward.

    The sections data will be provided via API calls and questions can show or hide
    dependent questions.

    Shows the <ReferralSuccessScreen> after Referral is submitted back to server.

    NOTE: this entire mechanism could also be an *interactive* series of screens.

    11/1 -  this page currently launches using masterService as the current service
            we may want to change this to use a route :id parameter

-->

<script>
    import { onMount } from 'svelte';
    import { fly, fade } from 'svelte/transition';
	import { masterSearch, masterService } from '../../common/master-objects.js';
    import { AccountStore } from "../../common/account";
	import { setErrorAlert } from '../../stores/AlertsStore';
    import { fetchReferralByServiceId, publicFetchReferralByServiceId } from '../../api/services.js';
    import { getIsPreferredPartner } from "../../common/utils.js";
    import { push } from 'svelte-spa-router';

    // TCARE components
    import ReferralBanner from '../../components/referrals/ReferralBanner.svelte';
    import ReferralInfo from '../../components/referrals/ReferralInfo.svelte';
    import ReferralSections from '../../components/referrals/ReferralSections.svelte';
    import ReferralDetails from '../../components/referrals/ReferralDetails.svelte';
    import ReferralSuccessScreen from '../../components/referrals/ReferralSuccessScreen.svelte';
	import AuthPostalBar from '../../components/searches/AuthPostalBar.svelte';     // needs masterSearch

    // TODO: replace these "hard-coded" sections with API calls!
    // NON-PARTNER REFERRALS - THIS WILL PROBABLY CHANGE WHEN THE API DOES
    import { SECTION_SEND_REFERRAL } from '../../components/referrals/fake-non-partner-referrals.js';
    import { SECTION_CONTACT_INFO } from '../../components/referrals/fake-non-partner-referrals.js';
    import { SECTION_SERVICE_SPECIFIC } from '../../components/referrals/fake-non-partner-referrals.js';

    // this is an object with id properties but could be an array 
    const FAKE_NON_PARTNER_SECTIONS = {
        "send-referral": SECTION_SEND_REFERRAL,
        "contact-info": SECTION_CONTACT_INFO,
        "service-specific": SECTION_SERVICE_SPECIFIC,
    };

    // PARTNER REFERRALS - THIS WILL PROBABLY CHANGE WHEN THE API DOES
    import { SECTION_PARTNER_SEND_REFERRAL } from '../../components/referrals/fake-partner-referrals.js';
    import { SECTION_PARTNER_CONTACT_INFO } from '../../components/referrals/fake-partner-referrals.js';
    import { SECTION_PARTNER_SERVICE_SPECIFIC } from '../../components/referrals/fake-partner-referrals.js';
    import { SECTION_PARTNER_ADDITIONAL_INFO } from '../../components/referrals/fake-partner-referrals.js';

    const FAKE_PARTNER_SECTIONS = {
        "send-referral": SECTION_PARTNER_SEND_REFERRAL,
        "contact-info": SECTION_PARTNER_CONTACT_INFO,
        "service-specific": SECTION_PARTNER_SERVICE_SPECIFIC,
        "additional-info": SECTION_PARTNER_ADDITIONAL_INFO
    }

    // SMUI components

    $: serviceData = $masterService.apiData;
    $: isFetchingReferral = false;
    $: isShowingDetails = false;
    $: isReferralSubmitted = false;
    $: isPreferredPartner = getIsPreferredPartner(serviceData);

    let json = null;
    let  sections = null;

    const FETCH_ERROR_MSG  = 'Error while fetching this referral.';

    onMount(() => {
        if (!serviceData['service-name']) {
            push('/')
        }

        // TODO: MUST FETCH THE SERVICE HERE - REWORK THIS!!!
        // DO NOT USE MASTER SERVICE !!!!

        // if (!$AccountStore?.isLoggedIn) {
        //     push('/');                                  // and login
        // }

        // Vito - are we fetching all sections at once here?
        /*
        isFetchingReferral = true;
        if ($AccountStore?.isLoggedIn) {
            json = await fetchReferralByServiceId(serviceData.id);
        } else {
            json = await publicFetchReferralByServiceId(serviceData.id);
        }
        isFetchingReferral = false;

        if (json.errors) {
            setErrorAlert(FETCH_ERROR_MSG, 2000);
        } else {
            if (isPreferredPartner) {
                sections = FAKE_PARTNER_SECTIONS;               // TODO: REMOVE THIS LINE!!!

            } else {
                sections = FAKE_NON_PARTNER_SECTIONS;           // TODO: REMOVE THIS LINE!!!
            }
            // sections = json;                                 // TODO: ENABLE THIS LINE!!!
        }
        */

        if (isPreferredPartner) {
            sections = FAKE_PARTNER_SECTIONS;                   // TODO: REMOVE THIS LINE!!!
        } else {
            sections = FAKE_NON_PARTNER_SECTIONS;               // TODO: REMOVE THIS LINE!!!
        }

        window.scrollTo(0, 0);          // do this for all other pages!
    })

    const showDetails = (bool) => {
        isShowingDetails = bool;
    }

    const setIsSubmitted = (bool) => {
        isReferralSubmitted = bool;
    }


</script>

<style>

    :global(.referrals-page .search-text-input.postal) {
        width: 110px;
    }

    @media screen and (max-width: 600px) {
    }

    @media screen and (min-width: 601px) and (max-width: 1300px) {
    }

    @media screen and (min-width: 1201px) {
    }

</style>

<div class="page-content referrals-page" in:fly="{{ x: 200, duration: 500 }}" out:fade="{{ duration: 250 }}">

    {#if serviceData['service-name']}
        <div class="spacer-h-25"></div>

        <div class="top-section">
<!--            <div class="flex-row">-->
<!--                <AuthPostalBar store={masterSearch} />-->
<!--            </div>-->

            <div class="flex-row">
                <div class="flex-block w-49">
                    <!-- left side of screen -->
                    <ReferralBanner {serviceData} {isReferralSubmitted} />
                </div>
                <div class="flex-block w-49">
                    <!-- right side of screen -->
                    <ReferralInfo {serviceData} {showDetails} {isReferralSubmitted} />
                </div>
            </div>
        </div>

        <div class="spacer-h-25"></div>

        <div class="bottom-section">
            {#if isReferralSubmitted}
                <ReferralSuccessScreen {serviceData} />
            {:else if isShowingDetails}
                <ReferralDetails store={masterService} {showDetails} />
            {:else}
                <!-- these are the expandable sections containing all the question components-->
                {#if sections}
                    <ReferralSections store={masterService} {sections} {setIsSubmitted} />
                {/if}
            {/if}
        </div>

        <div class="spacer-h-50"></div>
    {:else}
        <h3>No service name - please select a service for referral.</h3>
    {/if}
</div>
